import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CommsProvider } from './context/CommsContext';
import { LoginForm } from './components/shared/LoginForm';
import { ProtectedRoute } from './components/shared/ProtectedRoute';
import { LocaleLayout } from './components/shared/LocaleLayout';
import { LoadingSpinner } from './components/ui';
import { Toaster } from './components/ui/UseToast';


//Home
import {Home} from './pages/Home';
import {HomeCommunity} from './pages/HomeCommunity';
import {HomeParents} from './pages/HomeParents';
import {HomeEducators} from './pages/HomeEducators';
const  SubscriptionPlans = lazy(() => import('./pages/SubscriptionPlans'));
const PRVProspecting = lazy(() => import('./pages/PRVProspecting'));
const UserDashboard = lazy(() => import('./pages/UserDashboard'));


//Budget
const BudgetPlanner = lazy(() => import('./pages/budget/BudgetPlanner'));
const BudgetIncome = lazy(() => import('./pages/budget/BudgetIncome'));
const BudgetHousing = lazy(() => import('./pages/budget/BudgetHousing'));
const BudgetTransportation = lazy(() => import('./pages/budget/BudgetTransportation'));
const BudgetFood = lazy(() => import('./pages/budget/BudgetFood'));
const BudgetLiving = lazy(() => import('./pages/budget/BudgetLiving'));
const BudgetEntertainment = lazy(() => import('./pages/budget/BudgetEntertainment'));
const BudgetHealth = lazy(() => import('./pages/budget/BudgetHealth'));
const BudgetFinancial = lazy(() => import('./pages/budget/BudgetFinancial'));
const BudgetDebt = lazy(() => import('./pages/budget/BudgetDebt'));
const BudgetSummary = lazy(() => import('./pages/budget/BudgetSummary'));

//Budget Guides
const IncomeGuide = lazy(() => import('./pages/budget/guide/IncomeGuide'));
const HousingGuide = lazy(() => import('./pages/budget/guide/HousingGuide'));
const TransportationGuide = lazy(() => import('./pages/budget/guide/TransportationGuide'));
const FoodGuide = lazy(() => import('./pages/budget/guide/FoodGuide'));
const LivingGuide = lazy(() => import('./pages/budget/guide/LivingGuide'));
const EntertainmentGuide = lazy(() => import('./pages/budget/guide/EntertainmentGuide'));
const HealthGuide = lazy(() => import('./pages/budget/guide/HealthGuide'));
const FinancialGuide = lazy(() => import('./pages/budget/guide/FinancialGuide'));
const DebtGuide = lazy(() => import('./pages/budget/guide/DebtGuide'));
const SummaryGuide = lazy(() => import('./pages/budget/guide/SummaryGuide'));

//Credit Assessment
const CreditAssessmentPlanner = lazy(() => import('./pages/creditassessment/CreditAssessmentPlanner'));
const CreditProfile = lazy(() => import('./pages/creditassessment/CreditProfile'));
const CreditLines = lazy(() => import('./pages/creditassessment/CreditLines'));
const CreditUtilization = lazy(() => import('./pages/creditassessment/CreditUtilization'));
const CreditBudget = lazy(() => import('./pages/creditassessment/CreditBudget'));
const CreditDebtManagement = lazy(() => import('./pages/creditassessment/CreditDebtManagement'));
const CreditInterestAnalysis = lazy(() => import('./pages/creditassessment/CreditInterestAnalysis'));
const CreditHealthScore = lazy(() => import('./pages/creditassessment/CreditHealthScore'));
const CreditBuildingPlan = lazy(() => import('./pages/creditassessment/CreditBuildingPlan'));
const CreditGoals = lazy(() => import('./pages/creditassessment/CreditGoals'));
const CreditVisualization = lazy(() => import('./pages/creditassessment/CreditVisualization'));

//Food Truck
const FoodTruckPlanner = lazy(() => import('./pages/foodtruck/FoodTruckPlanner'));
const FoodTruckConcept = lazy(() => import('./pages/foodtruck/FoodTruck'));
const FoodTruckMenu = lazy(() => import('./pages/foodtruck/FoodTruckMenu'));
const FoodTruckExpectedSales = lazy(() => import('./pages/foodtruck/FoodTruckExpectedSales'));
const FoodTruckEquipment = lazy(() => import('./pages/foodtruck/FoodTruckEquipment'));
const FoodTruckWorkers = lazy(() => import('./pages/foodtruck/FoodTruckWorkers'));
const FoodTruckMarketing = lazy(() => import('./pages/foodtruck/FoodTruckMarketing'));
const FoodTruckInsurance = lazy(() => import('./pages/foodtruck/FoodTruckInsurance'));
const FoodTruckStartupCosts = lazy(() => import('./pages/foodtruck/FoodTruckStartupCosts'));
const FoodTruckBankCreditAccounts = lazy(() => import('./pages/foodtruck/FoodTruckBankCreditAccounts'));
const FoodTruckSummary = lazy(() => import('./pages/foodtruck/FoodTruckSummary'));

//Retirement
const RetirementCalcPlanner = lazy(() => import('./pages/retirement/RetirementCalcPlanner'));
const RetirementBasicInfo = lazy(() => import('./pages/retirement/Step1_BasicInfo'));
const RetirementAccounts = lazy(() => import('./pages/retirement/Step2_Accounts'));
const RetirementContributions = lazy(() => import('./pages/retirement/Step3_Contributions'));
const RetirementInvestments = lazy(() => import('./pages/retirement/Step4_Investments'));
const RetirementSocialSecurity = lazy(() => import('./pages/retirement/Step5_SocialSecurity'));
const RetirementGuaranteedIncome = lazy(() => import('./pages/retirement/Step6_GuaranteedIncome'));
const RetirementExpenses = lazy(() => import('./pages/retirement/Step7_Expenses'));
const RetirementHealthcare = lazy(() => import('./pages/retirement/Step8_Healthcare'));
const RetirementEstate = lazy(() => import('./pages/retirement/Step9_Estate'));
const RetirementResults = lazy(() => import('./pages/retirement/Step10_Results'));

//Understanding Taxes
const UnderstandingTaxesPlanner = lazy(() => import('./pages/understandingtaxes/UnderstandingTaxesPlanner'));
const TaxBasics = lazy(() => import('./pages/understandingtaxes/TaxBasics'));
const IncomeTaxCalculator = lazy(() => import('./pages/understandingtaxes/IncomeTaxCalculator'));
const TaxBrackets = lazy(() => import('./pages/understandingtaxes/TaxBrackets'));
const BasicTaxForms = lazy(() => import('./pages/understandingtaxes/BasicTaxForms'));
const TaxDeductions = lazy(() => import('./pages/understandingtaxes/TaxDeductions'));
const FilingStatus = lazy(() => import('./pages/understandingtaxes/FilingStatus'));
const PayrollTaxes = lazy(() => import('./pages/understandingtaxes/PayrollTaxes'));
const TaxCredits = lazy(() => import('./pages/understandingtaxes/TaxCredits'));
const StateTaxes = lazy(() => import('./pages/understandingtaxes/StateTaxes'));
const TaxSummary = lazy(() => import('./pages/understandingtaxes/TaxSummary'));

//Understanding Taxes - Guide
const TaxBasicsGuide = lazy(() => import('./pages/understandingtaxes/guide/TaxBasicsGuide'));
const TaxCalculatorGuide = lazy(() => import('./pages/understandingtaxes/guide/TaxCalculatorGuide'));
const TaxBracketsGuide = lazy(() => import('./pages/understandingtaxes/guide/TaxBracketsGuide'));
const BasicTaxFormsGuide = lazy(() => import('./pages/understandingtaxes/guide/BasicTaxFormsGuide'));
const TaxDeductionsGuide = lazy(() => import('./pages/understandingtaxes/guide/TaxDeductionsGuide'));
const FilingStatusGuide = lazy(() => import('./pages/understandingtaxes/guide/FilingStatusGuide'));
const PayrollTaxesGuide = lazy(() => import('./pages/understandingtaxes/guide/PayrollTaxesGuide'));
const TaxCreditsGuide = lazy(() => import('./pages/understandingtaxes/guide/TaxCreditsGuide'));
const StateTaxesGuide = lazy(() => import('./pages/understandingtaxes/guide/StateTaxesGuide'));
const TaxSummaryGuide = lazy(() => import('./pages/understandingtaxes/guide/TaxSummaryGuide'));

//Credit Assessment - Guide
const CreditProfileGuide = lazy(() => import('./pages/creditassessment/guide/CreditProfileGuide'));
const CreditLinesGuide = lazy(() => import('./pages/creditassessment/guide/CreditLinesGuide'));
const CreditUtilizationGuide = lazy(() => import('./pages/creditassessment/guide/CreditUtilizationGuide'));
const CreditBudgetGuide = lazy(() => import('./pages/creditassessment/guide/CreditBudgetGuide'));
const CreditDebtManagementGuide = lazy(() => import('./pages/creditassessment/guide/CreditDebtManagementGuide'));
const CreditInterestGuide = lazy(() => import('./pages/creditassessment/guide/CreditInterestGuide'));
const CreditHealthScoreGuide = lazy(() => import('./pages/creditassessment/guide/CreditHealthScoreGuide'));
const CreditBuildingPlanGuide = lazy(() => import('./pages/creditassessment/guide/CreditBuildingPlanGuide'));
const CreditGoalsGuide = lazy(() => import('./pages/creditassessment/guide/CreditGoalsGuide'));
const CreditVisualizationGuide = lazy(() => import('./pages/creditassessment/guide/CreditVisualizationGuide'));

//Food Truck - Guide
const FoodTruckConceptGuide = lazy(() => import('./pages/foodtruck/guide/FoodTruckConceptGuide'));
const FoodTruckMenuGuide = lazy(() => import('./pages/foodtruck/guide/FoodTruckMenuGuide'));
const FoodTruckSalesGuide = lazy(() => import('./pages/foodtruck/guide/FoodTruckSalesGuide'));
const FoodTruckEquipmentGuide = lazy(() => import('./pages/foodtruck/guide/FoodTruckEquipmentGuide'));
const FoodTruckWorkersGuide = lazy(() => import('./pages/foodtruck/guide/FoodTruckWorkersGuide'));
const FoodTruckMarketingGuide = lazy(() => import('./pages/foodtruck/guide/FoodTruckMarketingGuide'));
const FoodTruckInsuranceGuide = lazy(() => import('./pages/foodtruck/guide/FoodTruckInsuranceGuide'));
const FoodTruckStartupCostsGuide = lazy(() => import('./pages/foodtruck/guide/FoodTruckStartupCostsGuide'));
const FoodTruckBankingGuide = lazy(() => import('./pages/foodtruck/guide/FoodTruckBankingGuide'));
const FoodTruckSummaryGuide = lazy(() => import('./pages/foodtruck/guide/FoodTruckSummaryGuide'));

//Retirement - Guide
const RetirementBasicInfoGuide = lazy(() => import('./pages/retirement/guide/Step1_BasicInfoGuide'));
const RetirementAccountsGuide = lazy(() => import('./pages/retirement/guide/Step2_AccountsGuide'));
const RetirementContributionsGuide = lazy(() => import('./pages/retirement/guide/Step3_ContributionsGuide'));
const RetirementInvestmentsGuide = lazy(() => import('./pages/retirement/guide/Step4_InvestmentsGuide'));
const RetirementSocialSecurityGuide = lazy(() => import('./pages/retirement/guide/Step5_SocialSecurityGuide'));
const RetirementGuaranteedIncomeGuide = lazy(() => import('./pages/retirement/guide/Step6_GuaranteedIncomeGuide'));
const RetirementExpensesGuide = lazy(() => import('./pages/retirement/guide/Step7_ExpensesGuide'));
const RetirementHealthcareGuide = lazy(() => import('./pages/retirement/guide/Step8_HealthcareGuide'));
const RetirementEstateGuide = lazy(() => import('./pages/retirement/guide/Step9_EstateGuide'));
const RetirementResultsGuide = lazy(() => import('./pages/retirement/guide/Step10_ResultsGuide'));

//Elementary
const AllowanceSavings = lazy(() => import('./pages/elementary/AllowanceSavings_elem'));
const CharitableGiving = lazy(() => import('./pages/elementary/CharitableGiving_elem'));
const GoalSetting = lazy(() => import('./pages/elementary/GoalSetting_elem'));
const LemonadeStand = lazy(() => import('./pages/elementary/LemonadeStand_elem'));
const WantsNeeds = lazy(() => import('./pages/elementary/WantsNeeds_elem'));

//Admin
const UsersAdmin = lazy(() => import('./pages/UsersAdmin').then(module => ({ default: module.UsersAdmin })));
const NewsletterAdmin = lazy(() => import('./pages/NewsletterAdmin').then(module => ({ default: module.NewsletterAdmin })));


// Loading fallback component
const PageLoadingSpinner: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <LoadingSpinner size="lg" />
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);


// Main app content
const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  console.log('AppContent - isAuthenticated:', isAuthenticated, 'isLoading:', isLoading);

  if (isLoading) {
    return <PageLoadingSpinner />;
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/" element={<Navigate to="/en" replace />} />
        <Route path="/:locale" element={<LocaleLayout />}>
          <Route index element={<Home />} />
          <Route path="*" element={<Home />} />
        </Route>
      </Routes>
    );
  }

  console.log('User is authenticated, rendering main app');
  return (
    <CommsProvider>
      <Suspense fallback={<PageLoadingSpinner />}>
        <Routes>
          <Route path="/" element={<Navigate to="/en/dashboard" replace />} />
          <Route path="/:locale" element={<LocaleLayout />}>

          <Route
            path="home"
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <Home />
              </ProtectedRoute>
            }
          />
          <Route
            path="dashboard"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']}>
                <UserDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="home-community"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']}>
                <HomeCommunity />
              </ProtectedRoute>
            }
          />
          <Route
            path="home-parents"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']}>
                <HomeParents />
              </ProtectedRoute>
            }
          />
          <Route
            path="home-educators"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']}>
                <HomeEducators />
              </ProtectedRoute>
            }
          />
          <Route
            path="subscription-plans"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']}>
                <SubscriptionPlans />
              </ProtectedRoute>
            }
          />
          <Route
            path="prv-prospecting"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="prv_prospecting">
                <PRVProspecting />
              </ProtectedRoute>
            }
          />

          {/* BUDGET section */}
          <Route
            path="budget/budget-planner"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="budget">
                <BudgetPlanner />
              </ProtectedRoute>
            }
          />
          <Route
            path="budget/budget-income"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="budget">
                <BudgetIncome />
              </ProtectedRoute>
            }
          />
          <Route
            path="budget/budget-housing"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="budget">
                <BudgetHousing />
              </ProtectedRoute>
            }
          />
          <Route
            path="budget/budget-transportation"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="budget">
                <BudgetTransportation />
              </ProtectedRoute>
            }
          />
          <Route
            path="budget/budget-food"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="budget">
                <BudgetFood />
              </ProtectedRoute>
            }
          />
          <Route
            path="budget/budget-living"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="budget">
                <BudgetLiving />
              </ProtectedRoute>
            }
          />
          <Route
            path="budget/budget-entertainment"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="budget">
                <BudgetEntertainment />
              </ProtectedRoute>
            }
          />
          <Route
            path="budget/budget-health"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="budget">
                <BudgetHealth />
              </ProtectedRoute>
            }
          />
          <Route
            path="budget/budget-financial"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="budget">
                <BudgetFinancial />
              </ProtectedRoute>
            }
          />
          <Route
            path="budget/budget-debt"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="budget">
                <BudgetDebt />
              </ProtectedRoute>
            }
          />
          <Route
            path="budget/budget-summary"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="budget">
                <BudgetSummary />
              </ProtectedRoute>
            }
          />

          {/* BUDGET GUIDES section */}
          <Route
            path="budget/guide/income-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="budget">
                <IncomeGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="budget/guide/housing-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="budget">
                <HousingGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="budget/guide/transportation-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="budget">
                <TransportationGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="budget/guide/food-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="budget">
                <FoodGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="budget/guide/living-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="budget">
                <LivingGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="budget/guide/entertainment-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="budget">
                <EntertainmentGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="budget/guide/health-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="budget">
                <HealthGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="budget/guide/financial-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="budget">
                <FinancialGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="budget/guide/debt-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="budget">
                <DebtGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="budget/guide/summary-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="budget">
                <SummaryGuide />
              </ProtectedRoute>
            }
          />

          {/* CREDIT ASSESSMENT section */}
          <Route
            path="credit-assessment/credit-assessment-planner"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="credit_assessment">
                <CreditAssessmentPlanner />
              </ProtectedRoute>
            }
          />
          <Route
            path="credit-assessment/credit-profile"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="credit_assessment">
                <CreditProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="credit-assessment/credit-lines"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="credit_assessment">
                <CreditLines />
              </ProtectedRoute>
            }
          />
          <Route
            path="credit-assessment/credit-utilization"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="credit_assessment">
                <CreditUtilization />
              </ProtectedRoute>
            }
          />
          <Route
            path="credit-assessment/credit-budget"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="credit_assessment">
                <CreditBudget />
              </ProtectedRoute>
            }
          />
          <Route
            path="credit-assessment/credit-debt-management"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="credit_assessment">
                <CreditDebtManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="credit-assessment/credit-interest-analysis"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="credit_assessment">
                <CreditInterestAnalysis />
              </ProtectedRoute>
            }
          />
          <Route
            path="credit-assessment/credit-health-score"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="credit_assessment">
                <CreditHealthScore />
              </ProtectedRoute>
            }
          />
          <Route
            path="credit-assessment/credit-building-plan"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="credit_assessment">
                <CreditBuildingPlan />
              </ProtectedRoute>
            }
          />
          <Route
            path="credit-assessment/credit-goals"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="credit_assessment">
                <CreditGoals />
              </ProtectedRoute>
            }
          />
          <Route
            path="credit-assessment/credit-visualization"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="credit_assessment">
                <CreditVisualization />
              </ProtectedRoute>
            }
          />

          {/* FOOD TRUCK section */}
          <Route
            path="food-truck/food-truck-planner"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="food_truck">
                <FoodTruckPlanner />
              </ProtectedRoute>
            }
          />
          <Route
            path="food-truck/food-truck-concept"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="food_truck">
                <FoodTruckConcept />
              </ProtectedRoute>
            }
          />
          <Route
            path="food-truck/food-truck-menu"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="food_truck">
                <FoodTruckMenu />
              </ProtectedRoute>
            }
          />
          <Route
            path="food-truck/food-truck-expected-sales"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="food_truck">
                <FoodTruckExpectedSales />
              </ProtectedRoute>
            }
          />
          <Route
            path="food-truck/food-truck-equipment"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="food_truck">
                <FoodTruckEquipment />
              </ProtectedRoute>
            }
          />
          <Route
            path="food-truck/food-truck-workers"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="food_truck">
                <FoodTruckWorkers />
              </ProtectedRoute>
            }
          />
          <Route
            path="food-truck/food-truck-marketing"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="food_truck">
                <FoodTruckMarketing />
              </ProtectedRoute>
            }
          />
          <Route
            path="food-truck/food-truck-insurance"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="food_truck">
                <FoodTruckInsurance />
              </ProtectedRoute>
            }
          />
          <Route
            path="food-truck/food-truck-startup-costs"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="food_truck">
                <FoodTruckStartupCosts />
              </ProtectedRoute>
            }
          />
          <Route
            path="food-truck/food-truck-bank-credit-accounts"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="food_truck">
                <FoodTruckBankCreditAccounts />
              </ProtectedRoute>
            }
          />
          <Route
            path="food-truck/food-truck-summary"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="food_truck">
                <FoodTruckSummary />
              </ProtectedRoute>
            }
          />

          {/* RETIREMENT section */}
          <Route
            path="retirement/retirement-calc-planner"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="retirement">
                <RetirementCalcPlanner />
              </ProtectedRoute>
            }
          />
          <Route
            path="retirement/basic-info"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="retirement">
                <RetirementBasicInfo />
              </ProtectedRoute>
            }
          />
          <Route
            path="retirement/accounts"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="retirement">
                <RetirementAccounts />
              </ProtectedRoute>
            }
          />
          <Route
            path="retirement/contributions"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="retirement">
                <RetirementContributions />
              </ProtectedRoute>
            }
          />
          <Route
            path="retirement/investments"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="retirement">
                <RetirementInvestments />
              </ProtectedRoute>
            }
          />
          <Route
            path="retirement/social-security"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="retirement">
                <RetirementSocialSecurity />
              </ProtectedRoute>
            }
          />
          <Route
            path="retirement/guaranteed-income"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="retirement">
                <RetirementGuaranteedIncome />
              </ProtectedRoute>
            }
          />
          <Route
            path="retirement/expenses"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="retirement">
                <RetirementExpenses />
              </ProtectedRoute>
            }
          />
          <Route
            path="retirement/healthcare"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="retirement">
                <RetirementHealthcare />
              </ProtectedRoute>
            }
          />
          <Route
            path="retirement/estate"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="retirement">
                <RetirementEstate />
              </ProtectedRoute>
            }
          />
          <Route
            path="retirement/results"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="retirement">
                <RetirementResults />
              </ProtectedRoute>
            }
          />

          {/* RETIREMENT GUIDES section */}
          <Route
            path="retirement/guide/basic-info-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="retirement">
                <RetirementBasicInfoGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="retirement/guide/accounts-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="retirement">
                <RetirementAccountsGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="retirement/guide/contributions-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="retirement">
                <RetirementContributionsGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="retirement/guide/investments-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="retirement">
                <RetirementInvestmentsGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="retirement/guide/social-security-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="retirement">
                <RetirementSocialSecurityGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="retirement/guide/guaranteed-income-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="retirement">
                <RetirementGuaranteedIncomeGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="retirement/guide/expenses-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="retirement">
                <RetirementExpensesGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="retirement/guide/healthcare-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="retirement">
                <RetirementHealthcareGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="retirement/guide/estate-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="retirement">
                <RetirementEstateGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="retirement/guide/results-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="retirement">
                <RetirementResultsGuide />
              </ProtectedRoute>
            }
          />

          {/* UNDERSTANDING TAXES section */}
          <Route
            path="understanding-taxes/understanding-taxes-planner"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="tax">
                <UnderstandingTaxesPlanner />
              </ProtectedRoute>
            }
          />
          <Route
            path="understanding-taxes/tax-basics"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="tax">
                <TaxBasics />
              </ProtectedRoute>
            }
          />
          <Route
            path="understanding-taxes/income-tax-calculator"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="tax">
                <IncomeTaxCalculator />
              </ProtectedRoute>
            }
          />
          <Route
            path="understanding-taxes/tax-brackets"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="tax">
                <TaxBrackets />
              </ProtectedRoute>
            }
          />
          <Route
            path="understanding-taxes/basic-tax-forms"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="tax">
                <BasicTaxForms />
              </ProtectedRoute>
            }
          />
          <Route
            path="understanding-taxes/tax-deductions"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="tax">
                <TaxDeductions />
              </ProtectedRoute>
            }
          />
          <Route
            path="understanding-taxes/filing-status"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="tax">
                <FilingStatus />
              </ProtectedRoute>
            }
          />
          <Route
            path="understanding-taxes/payroll-taxes"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="tax">
                <PayrollTaxes />
              </ProtectedRoute>
            }
          />
          <Route
            path="understanding-taxes/tax-credits"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="tax">
                <TaxCredits />
              </ProtectedRoute>
            }
          />
          <Route
            path="understanding-taxes/state-taxes"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="tax">
                <StateTaxes />
              </ProtectedRoute>
            }
          />
          <Route
            path="understanding-taxes/tax-summary"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="tax">
                <TaxSummary />
              </ProtectedRoute>
            }
          />

          {/* UNDERSTANDING TAXES GUIDES section */}
          <Route
            path="understanding-taxes/guide/tax-basics-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="tax">
                <TaxBasicsGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="understanding-taxes/guide/tax-calculator-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="tax">
                <TaxCalculatorGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="understanding-taxes/guide/tax-brackets-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="tax">
                <TaxBracketsGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="understanding-taxes/guide/basic-tax-forms-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="tax">
                <BasicTaxFormsGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="understanding-taxes/guide/tax-deductions-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="tax">
                <TaxDeductionsGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="understanding-taxes/guide/filing-status-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="tax">
                <FilingStatusGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="understanding-taxes/guide/payroll-taxes-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="tax">
                <PayrollTaxesGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="understanding-taxes/guide/tax-credits-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="tax">
                <TaxCreditsGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="understanding-taxes/guide/state-taxes-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="tax">
                <StateTaxesGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="understanding-taxes/guide/tax-summary-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="tax">
                <TaxSummaryGuide />
              </ProtectedRoute>
            }
          />

          {/* FOOD TRUCK GUIDES section */}
          <Route
            path="food-truck/guide/food-truck-concept-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="food_truck">
                <FoodTruckConceptGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="food-truck/guide/food-truck-menu-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="food_truck">
                <FoodTruckMenuGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="food-truck/guide/food-truck-sales-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="food_truck">
                <FoodTruckSalesGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="food-truck/guide/food-truck-equipment-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="food_truck">
                <FoodTruckEquipmentGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="food-truck/guide/food-truck-workers-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="food_truck">
                <FoodTruckWorkersGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="food-truck/guide/food-truck-marketing-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="food_truck">
                <FoodTruckMarketingGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="food-truck/guide/food-truck-insurance-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="food_truck">
                <FoodTruckInsuranceGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="food-truck/guide/food-truck-startup-costs-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="food_truck">
                <FoodTruckStartupCostsGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="food-truck/guide/food-truck-banking-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="food_truck">
                <FoodTruckBankingGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="food-truck/guide/food-truck-summary-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="food_truck">
                <FoodTruckSummaryGuide />
              </ProtectedRoute>
            }
          />

          {/* CREDIT ASSESSMENT GUIDES section */}
          <Route
            path="credit-assessment/guide/credit-profile-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="credit_assessment">
                <CreditProfileGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="credit-assessment/guide/credit-lines-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="credit_assessment">
                <CreditLinesGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="credit-assessment/guide/credit-utilization-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="credit_assessment">
                <CreditUtilizationGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="credit-assessment/guide/credit-budget-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="credit_assessment">
                <CreditBudgetGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="credit-assessment/guide/credit-debt-management-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="credit_assessment">
                <CreditDebtManagementGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="credit-assessment/guide/credit-interest-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="credit_assessment">
                <CreditInterestGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="credit-assessment/guide/credit-health-score-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="credit_assessment">
                <CreditHealthScoreGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="credit-assessment/guide/credit-building-plan-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="credit_assessment">
                <CreditBuildingPlanGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="credit-assessment/guide/credit-goals-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="credit_assessment">
                <CreditGoalsGuide />
              </ProtectedRoute>
            }
          />
          <Route
            path="credit-assessment/guide/credit-visualization-guide"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredModule="credit_assessment">
                <CreditVisualizationGuide />
              </ProtectedRoute>
            }
          />

          {/* ELEMENTARY section */}
          <Route
            path="elementary/allowance-savings"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredElementaryItem="allowance_savings">
                <AllowanceSavings />
              </ProtectedRoute>
            }
          />
          <Route
            path="elementary/charitable-giving"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredElementaryItem="charitable_giving">
                <CharitableGiving />
              </ProtectedRoute>
            }
          />
          <Route
            path="elementary/goal-setting"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredElementaryItem="goal_setting">
                <GoalSetting />
              </ProtectedRoute>
            }
          />
          <Route
            path="elementary/lemonade-stand"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredElementaryItem="lemonade_stand">
                <LemonadeStand />
              </ProtectedRoute>
            }
          />
          <Route
            path="elementary/wants-needs"
            element={
              <ProtectedRoute requiredRoles={['admin', 'user']} requiredElementaryItem="wants_needs">
                <WantsNeeds />
              </ProtectedRoute>
            }
          />

          {/* ADMINISTRATION section - Admin only */}
          <Route
            path="users-admin"
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <UsersAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            path="newsletter-admin"
            element={
              <ProtectedRoute requiredRoles={['admin']}>
                <NewsletterAdmin />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<Navigate to="dashboard" replace />} />
          </Route>
        </Routes>
      </Suspense>
    </CommsProvider>
  );
};

// Main App component
const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <div className="min-h-screen bg-gray-50">
          <AppContent />
          <Toaster />
        </div>
      </AuthProvider>
    </Router>
  );
};

export default App;
